import { createAsyncThunk } from "@reduxjs/toolkit";
import apiCall, { getTokens } from "../AuthorizedApi";
import { getHostUrl } from "../getHostUrl";

const buildQueryUrl = (base: string, params?: { page?: number; page_size?: number; search?: string; branch_id?: number | null }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    if (params?.search) queryParams.append('search', params.search);
    if (params?.branch_id !== undefined && params?.branch_id !== null && params.branch_id !== ('' as any)) {
        queryParams.append('branch_id', String(params.branch_id));
    }
    const queryString = queryParams.toString();
    return `${base}${queryString ? '?' + queryString : ''}`;
};

export const createOrderAPI = createAsyncThunk(
    'order/createOrderAPI',
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await apiCall({
                method: 'POST',
                url: `/order/orders/`,
                data
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getAllOrdersAPI = createAsyncThunk(
    'order/getAllOrdersAPI',
    async (params: { page?: number; page_size?: number; search?: string; branch_id?: number | null } | undefined, { rejectWithValue }) => {
        try {
            const url = buildQueryUrl('/order/orders/', params);
            const response = await apiCall({
                method: 'GET',
                url
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getOrderByIdAPI = async (id: string | number) => {
    return apiCall({
        method: 'GET',
        url: `/order/orders/${id}/`
    });
};

export const updateOrderAPI = async (id: string | number, data: any) => {
    return apiCall({
        method: 'PUT',
        url: `/order/orders/${id}/`,
        data
    });
};

export const deleteOrderAPI = async (id: string | number) => {
    return apiCall({
        method: 'DELETE',
        url: `/order/orders/${id}/`
    });
};

export const getOrderNextNumberAPI = async () => {
    return apiCall({
        method: 'GET',
        url: `/order/orders/next-number/`
    });
};

export const getOrderDashboardStatsAPI = async (params?: { from_date?: string; to_date?: string; branch_id?: number | null }) => {
    const queryParams = new URLSearchParams();
    if (params?.from_date) queryParams.append('from_date', params.from_date);
    if (params?.to_date) queryParams.append('to_date', params.to_date);
    if (params?.branch_id !== undefined && params?.branch_id !== null && params.branch_id !== ('' as any)) {
        queryParams.append('branch_id', String(params.branch_id));
    }

    const queryString = queryParams.toString();
    const url = `/order/dashboard/stats/${queryString ? '?' + queryString : ''}`;

    return apiCall({
        method: 'GET',
        url
    });
};

export const getOrderTransactionsAPI = async (params?: { page?: number; page_size?: number; search?: string; branch_id?: number | null }) => {
    const url = buildQueryUrl('/order/transactions/', params);
    return apiCall({ method: 'GET', url });
};

export const createOrderTransactionAPI = async (data: any) => {
    return apiCall({ method: 'POST', url: '/order/transactions/', data });
};

export const updateOrderTransactionAPI = async (id: number | string, data: any) => {
    return apiCall({ method: 'PUT', url: `/order/transactions/${id}/`, data });
};

export const deleteOrderTransactionAPI = async (id: number | string) => {
    return apiCall({ method: 'DELETE', url: `/order/transactions/${id}/` });
};

export const getOrderWorkAssignmentsAPI = async (params?: { page?: number; page_size?: number; search?: string; branch_id?: number | null }) => {
    const url = buildQueryUrl('/order/work-assignments/', params);
    return apiCall({ method: 'GET', url });
};

export const createOrderWorkAssignmentAPI = async (data: any) => {
    return apiCall({ method: 'POST', url: '/order/work-assignments/', data });
};

export const updateOrderWorkAssignmentAPI = async (id: number | string, data: any) => {
    return apiCall({ method: 'PUT', url: `/order/work-assignments/${id}/`, data });
};

export const deleteOrderWorkAssignmentAPI = async (id: number | string) => {
    return apiCall({ method: 'DELETE', url: `/order/work-assignments/${id}/` });
};

export const shareOrderOnWhatsAppAPI = async (
    orderId: number | string,
    pdfBlob: Blob,
    filename: string
): Promise<any> => {
    const { accessToken } = getTokens();
    const form = new FormData();
    form.append("pdf", pdfBlob, filename);
    form.append("template_category", "utility");

    const response = await fetch(
        `${getHostUrl()}/order/orders/${orderId}/share-whatsapp/`,
        {
            method: "POST",
            headers: { Authorization: `Bearer ${accessToken}` },
            body: form,
        }
    );

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data?.message || data?.error?.message || "Share failed");
    }

    return data;
};


export const notifyWorkAssignmentWhatsAppAPI = async (id: number | string, data?: any) =>
  apiCall({ method: 'POST', url: `/order/work-assignments/${id}/notify-whatsapp/`, data: data || {} });

export const notifyTeamMemberAPI = async (orderId: number | string, data: { team_member_id: number; order_event_id?: number; role?: string; phone?: string }) =>
  apiCall({ method: 'POST', url: `/order/orders/${orderId}/notify-team-member/`, data });

export const notifyTeamAPI = async (orderId: number | string, data: { team_member_ids?: number[]; assignment_ids?: number[]; order_event_id?: number }) =>
  apiCall({ method: 'POST', url: `/order/orders/${orderId}/notify-team/`, data });

export const getOrderSessionsAPI = async () => {
  return apiCall({ method: 'GET', url: '/order/sessions/' });
};

export const getTeamMemberAvailabilityAPI = async (params: {
  event_date?: string;
  event_session?: string;
  order_event?: number | string;
  order?: number | string;
  team_member?: number | string;
  exclude_assignment?: number | string;
}) => {
  const queryParams = new URLSearchParams();
  if (params.event_date) queryParams.append('event_date', params.event_date);
  if (params.event_session) queryParams.append('event_session', params.event_session);
  if (params.order_event) queryParams.append('order_event', String(params.order_event));
  if (params.order) queryParams.append('order', String(params.order));
  if (params.team_member) queryParams.append('team_member', String(params.team_member));
  if (params.exclude_assignment) queryParams.append('exclude_assignment', String(params.exclude_assignment));

  const queryString = queryParams.toString();
  return apiCall({ method: 'GET', url: `/order/team-member-availability/${queryString ? '?' + queryString : ''}` });
};


